import { t } from "@lingui/macro"
import React from "react"

import { createLoadingKeys } from "src/lib/loading"
import { DEFAULT_ACCESS_GROUP } from "src/config"
import { Pagination } from "src/lib/pagination"
import { ChatbotAdminService } from "src/api"
import { IAgentConfigItem } from "src/views/chatbot-configuration/types"
import { loads } from "src/channel/utils"
import { IAdvanceQueryModel } from "src/types/data-grid-pro"
import { CustomDataHelper } from "src/api/_custom/services/DataHelper"
import { reportError } from "src/lib/report"

import { Channel } from "src/channel"

export class ChatbotConfigurationStore implements IDisposable {
    static Context = React.createContext<ChatbotConfigurationStore | null>(null)
    static LoadingKeys = createLoadingKeys("init", "loading", "getAssignees")

    private repositoryUpdatesListenerDisposer?: () => void

    _accessGroupId: number = DEFAULT_ACCESS_GROUP.id
    _segments: number[] = []

    chatbotConfigurationItems = new Pagination(
        async (query) => {
            const response =
                await ChatbotAdminService.postV1AdminChatbotAgentConfigList({
                    request: {
                        page_number: query.page,
                        page_size: query.pageSize,
                        filter: query.advanceQuery,
                        ...CustomDataHelper.PrepareVisibilityData(this),
                    },
                })
            const items: IAgentConfigItem[] =
                response?.configs?.map((item) => ({
                    id: item.feature_config_id ?? 0,
                    feature_config_id: item.feature_config_id ?? 0,
                    access_group_id: item.access_group_id ?? 0,
                    access_type: item.access_type ?? "NONE",
                    agent_config_id: item.feature_config_id ?? 0,
                    config: item.config ?? {},
                    name: item.name ?? "",
                    segment_ids: item.segment_ids ?? [],
                    segment_names: item.segment_names ?? [],
                })) ?? []

            return {
                items,
                count: response?.configs?.length ?? 0,
            }
        },
        { loadingKey: ChatbotConfigurationStore.LoadingKeys.loading },
    )

    dispose() {
        this.repositoryUpdatesListenerDisposer?.()
    }

    get segments() {
        return this._segments
    }

    private setSegments(segments: number[]) {
        this._segments = segments
    }

    async loadSegments(segments: number[]) {
        await this.setSegments(segments)
        await this.chatbotConfigurationItems.loadInitialPage()
    }

    private setAccessGroupId(accessGroupId: number) {
        this._accessGroupId = accessGroupId
    }

    @loads(() => ChatbotConfigurationStore.LoadingKeys.init)
    async init(accessGroupId: number, advanceQuery?: IAdvanceQueryModel) {
        this.listenToMessageRepositoryUpdated()
        this.setAccessGroupId(accessGroupId)
        await this.chatbotConfigurationItems.loadInitialPage(advanceQuery)
    }

    async query(advanceQuery: IAdvanceQueryModel) {
        await this.chatbotConfigurationItems.loadAdvanceQuery(advanceQuery)
    }

    private listenToMessageRepositoryUpdated() {
        this.repositoryUpdatesListenerDisposer = Channel.addListener(
            async (event) => {
                if (
                    event.name === "repository/updated" &&
                    event.payload.repository === "chatbot-configuration"
                ) {
                    await this.chatbotConfigurationItems.reload()
                }
            },
        )
    }

    deleteItem = async (configId: string) => {
        try {
            await ChatbotAdminService.deleteV1AdminChatbotAgentConfig({
                configId,
            })
        } catch (error) {
            reportError(
                t`chatbot-configuration-view.store.failed-to-delete-item`,
                error,
            )
        } finally {
            await this.chatbotConfigurationItems.reload()
        }
    }
}
